import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { Avatar, SelectPaginate, SelectPaginateOption } from 'ui';
import { HolderSelectionMode } from 'api';

interface HoldersSelectPropsSingle {
  isMulti: false;
  label?: string;
  value: number | null;
  disabled?: boolean;
  error?: string;
  useEntityId?: boolean;
  onChange: (option: number | null) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  className?: string;
  disableWhileLoading?: boolean;
  dividendEventId?: number;
  onOptionsLoaded?: (payload: { dividendEventId?: number; optionValues: number[] }) => void;
}

interface HoldersSelectPropsMulti {
  isMulti: true;
  label?: string;
  value: number[];
  selectionMode?: HolderSelectionMode;
  showSelectionModeShortcuts?: boolean;
  disabled?: boolean;
  error?: string;
  useEntityId?: boolean;
  onChange: (option: number[] | null) => void;
  onSelectionModeChange?: (mode: HolderSelectionMode) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  className?: string;
  disableWhileLoading?: boolean;
  dividendEventId?: number;
  onOptionsLoaded?: (payload: { dividendEventId?: number; optionValues: number[] }) => void;
  selectionModeShortcuts?: HolderSelectionShortcut[];
}

type HoldersSelectProps = HoldersSelectPropsSingle | HoldersSelectPropsMulti;
type HolderSelectionShortcut = HolderSelectionMode.AllHolders | HolderSelectionMode.NonZeroHolders;

type HolderOptionValue = number | HolderSelectionMode;
type HolderSelectOption = SelectPaginateOption<HolderOptionValue> & {
  searchText: string;
};

const HoldersSelect = ({
  isMulti = false,
  value,
  disabled,
  error,
  className,
  label = 'Holder',
  useEntityId = false,
  disableWhileLoading = false,
  dividendEventId,
  onOptionsLoaded,
  onChange,
  onMenuOpen,
  onMenuClose,
  ...props
}: HoldersSelectProps) => {
  const [params] = useSearchParams();
  const register_id = Number(params.get(REGISTER_ID_SEARCH_PARAM_KEY));

  const {
    data: registerShareholderList,
    isError: isRegisterShareholderListError,
    isFetching: isRegisterShareholderListFetching,
    isLoading: isRegisterShareholderListLoading,
  } = useRegisterShareholderList(
    { register_id, ...(dividendEventId ? { dividend_event_id: dividendEventId } : {}) },
    { disabled: !register_id },
  );
  const lastReportedOptionsRef = useRef<string>();
  const currentFilterKey = `${register_id || 'none'}:${dividendEventId ?? 'none'}`;
  const isRegisterShareholderListPending =
    isRegisterShareholderListLoading || isRegisterShareholderListFetching;
  const isRegisterShareholderListDependentLoading =
    isRegisterShareholderListLoading ||
    (isRegisterShareholderListFetching && !registerShareholderList);
  const [resolvedRegisterShareholderListOptions, setResolvedRegisterShareholderListOptions] =
    useState<HolderSelectOption[]>([]);
  const [resolvedRegisterShareholderListOptionsKey, setResolvedRegisterShareholderListOptionsKey] =
    useState<string>();

  useEffect(() => {
    lastReportedOptionsRef.current = undefined;
    setResolvedRegisterShareholderListOptions([]);
    setResolvedRegisterShareholderListOptionsKey(undefined);
  }, [currentFilterKey]);

  const registerShareholderListOptions = useMemo(
    () =>
      registerShareholderList?.shareholders?.map(
        ({ id, entity_id, entity: { entity_name }, entity_details, user }) => {
          const entityName =
            'business_name_trustee' in (entity_details?.investor_details || {}) &&
            !!entity_details?.investor_details?.business_name_trustee
              ? `${entity_details?.investor_details?.business_name_trustee} <${entity_name}>`
              : `${entity_name}`;
          const firstName = user?.first_name || entityName;
          const lastName = user?.last_name || '';

          return {
            label: entityName,
            value: useEntityId ? entity_id : id,
            avatar: (
              <Avatar
                withBorder
                size="sm"
                className="border-[#C4C4C4]"
                classNameLetters="text-[12px]"
                firstName={firstName}
                lastName={lastName}
                src={user?.avatar_url || undefined}
              />
            ),
            searchText: `${entityName} ${user?.first_name || ''} ${user?.last_name || ''} ${
              user?.email || ''
            }`
              .trim()
              .toLowerCase(),
          };
        },
      ) || [],
    [registerShareholderList?.shareholders, useEntityId],
  );

  useEffect(() => {
    if (
      isRegisterShareholderListPending ||
      isRegisterShareholderListError ||
      !registerShareholderList
    ) {
      return;
    }

    setResolvedRegisterShareholderListOptions(
      registerShareholderListOptions as HolderSelectOption[],
    );
    setResolvedRegisterShareholderListOptionsKey(currentFilterKey);
  }, [
    currentFilterKey,
    isRegisterShareholderListError,
    isRegisterShareholderListPending,
    registerShareholderList,
    registerShareholderListOptions,
  ]);

  const filterScopedRegisterShareholderListOptions = useMemo(
    () =>
      resolvedRegisterShareholderListOptionsKey === currentFilterKey
        ? resolvedRegisterShareholderListOptions
        : [],
    [
      currentFilterKey,
      resolvedRegisterShareholderListOptions,
      resolvedRegisterShareholderListOptionsKey,
    ],
  );

  useEffect(() => {
    if (resolvedRegisterShareholderListOptionsKey !== currentFilterKey) {
      return;
    }

    const optionValues = filterScopedRegisterShareholderListOptions.map(
      option => option.value as number,
    );
    const nextOptionsKey = `${currentFilterKey}:${optionValues.join(',')}`;
    if (lastReportedOptionsRef.current === nextOptionsKey) {
      return;
    }

    lastReportedOptionsRef.current = nextOptionsKey;
    onOptionsLoaded?.({
      dividendEventId,
      optionValues,
    });
  }, [
    currentFilterKey,
    filterScopedRegisterShareholderListOptions,
    onOptionsLoaded,
    resolvedRegisterShareholderListOptionsKey,
  ]);

  const selectedSingleValue = useMemo(() => {
    if (isMulti) {
      return undefined;
    }

    return filterScopedRegisterShareholderListOptions.find(option => option.value === value) as
      | SelectPaginateOption<number>
      | undefined;
  }, [filterScopedRegisterShareholderListOptions, isMulti, value]);

  const selectedMultiValue = useMemo(() => {
    if (!isMulti) {
      return [];
    }

    const multiValue = value as number[];
    return filterScopedRegisterShareholderListOptions.filter(option =>
      multiValue.includes(option.value as number),
    );
  }, [filterScopedRegisterShareholderListOptions, isMulti, value]);

  const handleLoadOptions = async (
    page: number,
    search?: string,
  ): Promise<HolderSelectOption[]> => {
    if (page > 1) return [];
    if (
      isRegisterShareholderListDependentLoading ||
      resolvedRegisterShareholderListOptionsKey !== currentFilterKey
    ) {
      return [];
    }

    const normalizedSearch = search?.trim().toLowerCase();
    if (!normalizedSearch) {
      return filterScopedRegisterShareholderListOptions as HolderSelectOption[];
    }

    return (filterScopedRegisterShareholderListOptions as HolderSelectOption[]).filter(option =>
      option.searchText.includes(normalizedSearch),
    );
  };

  const handleLoadSingleOptions = async (
    page: number,
    search?: string,
  ): Promise<SelectPaginateOption<number>[]> => {
    const options = await handleLoadOptions(page, search);
    return options as SelectPaginateOption<number>[];
  };

  if (isMulti) {
    const multiProps = props as Pick<
      HoldersSelectPropsMulti,
      | 'selectionMode'
      | 'showSelectionModeShortcuts'
      | 'onSelectionModeChange'
      | 'selectionModeShortcuts'
    >;
    const multiOnChange = onChange as HoldersSelectPropsMulti['onChange'];
    const shortcutModes = multiProps.showSelectionModeShortcuts
      ? multiProps.selectionModeShortcuts || [
          HolderSelectionMode.AllHolders,
          HolderSelectionMode.NonZeroHolders,
        ]
      : [];
    const shortcutOptions: HolderSelectOption[] = shortcutModes.map(mode => ({
      label:
        mode === HolderSelectionMode.AllHolders
          ? 'All holders'
          : 'All holders with non-zero holdings',
      value: mode,
      searchText:
        mode === HolderSelectionMode.AllHolders
          ? 'all holders'
          : 'all holders with non-zero holdings non zero holders',
    }));
    const selectionMode = multiProps.selectionMode || HolderSelectionMode.SelectedHolders;
    const selectedValues =
      selectionMode === HolderSelectionMode.SelectedHolders
        ? selectedMultiValue
        : shortcutOptions.filter(option => option.value === selectionMode);

    return (
      <SelectPaginate<HolderOptionValue, true>
        isMulti={true}
        cacheUniqs={[register_id || null, dividendEventId ?? null]}
        label={label}
        disabled={disabled || (disableWhileLoading && isRegisterShareholderListDependentLoading)}
        isLoading={isRegisterShareholderListPending}
        error={error}
        value={selectedValues}
        className={className}
        isClearable={true}
        showSelectedOptionsInMenu={true}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onAsync={async (page, search) => {
          const holderOptions = await handleLoadOptions(page, search);
          if (page > 1) return [];
          return [...shortcutOptions, ...holderOptions];
        }}
        onChange={options => {
          const selectedOptions = (options || []) as HolderSelectOption[];
          const lastSelectedValue = selectedOptions.at(-1)?.value;

          if (
            lastSelectedValue === HolderSelectionMode.AllHolders ||
            lastSelectedValue === HolderSelectionMode.NonZeroHolders
          ) {
            multiOnChange([]);
            multiProps.onSelectionModeChange?.(lastSelectedValue);
            return;
          }

          multiProps.onSelectionModeChange?.(HolderSelectionMode.SelectedHolders);
          multiOnChange(
            selectedOptions
              .filter(
                option =>
                  option.value !== HolderSelectionMode.AllHolders &&
                  option.value !== HolderSelectionMode.NonZeroHolders,
              )
              .map(option => option.value as number),
          );
        }}
      />
    );
  }

  const singleOnChange = onChange as HoldersSelectPropsSingle['onChange'];

  return (
    <SelectPaginate<number, false>
      isMulti={false}
      cacheUniqs={[register_id || null, dividendEventId ?? null]}
      label={label}
      disabled={disabled || (disableWhileLoading && isRegisterShareholderListDependentLoading)}
      isLoading={isRegisterShareholderListPending}
      error={error}
      value={selectedSingleValue || null}
      className={className}
      isClearable={true}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      onAsync={handleLoadSingleOptions}
      onChange={options => {
        singleOnChange((options?.value ?? null) as number | null);
      }}
    />
  );
};

export default HoldersSelect;
